<template>
  <div class="row">
    <div class="col-12 col-lg-8">
      <h2 class="mb-4" v-text="getUserFullName()" />
      <a-form ref="form">
        <a-list-item-with-navigation
          icon="address-card-o"
          :title="$t('Config.UserProfile.ProfilePreferences')"
          :subtitle="$t('Config.UserProfile.SubtitleProfilePreferences')"
          navigation-route="/config/userprofile/profil-preferences"
        />
        <hr class="m-0" />
        <template v-if="passwordCanBeChanged">
          <a-list-item-with-navigation
            icon="unlock-alt"
            :title="$t('Config.UserProfile.ChangePassword')"
            :subtitle="$t('Config.UserProfile.SubtitleChangePassword')"
            navigation-route="/config/userprofile/change-password"
          />
          <hr class="m-0" />
        </template>
        <a-list-item-with-action
          icon="language"
          :title="$t('Config.UserProfile.ChooseLanguage')"
          :subtitle="$t('Config.UserProfile.SubTitleChooseLanguage')"
        >
          <a-select-2
            v-model="selectedLanguage"
            :options="languages"
            :settings="{
              templateResult: renderResult,
              templateSelection: renderResult,
              theme: 'bootstrap'
            }"
            @change="confirmReload = true"
          />
        </a-list-item-with-action>
        <hr class="m-0" />
        <a-list-item-with-action
          icon="euro"
          :title="$t('Config.UserProfile.ChangeCurrency')"
          :subtitle="$t('Config.UserProfile.SubTitleChangeCurrency')"
        >
          <a-select-2
            v-model="selectedCurrency"
            :options="currencies"
            :settings="{
              theme: 'bootstrap'
            }"
            @change="setActiveCurrency"
          />
        </a-list-item-with-action>
      </a-form>
    </div>
    <a-modal-dialg v-model="confirmReload" max-width="800px">
      <span
        slot="header"
        v-t="'Config.UserProfile.LanguageChanged'"
        class="h5"
      />
      <div slot="body" v-t="'Config.UserProfile.LanguageChanged.Info'" />
      <div slot="footer" class="d-flex justify-content-end">
        <button
          v-t="'Config.UserProfile.LanguageChanged.Cancel'"
          type="button"
          class="btn btn-pf-secondary"
          @click="confirmAbort"
        />
        <button
          v-t="'Config.UserProfile.LanguageChanged.Logout'"
          type="button"
          class="btn btn-pf-primary ml-2"
          @click="setActiveLanguage"
        />
      </div>
    </a-modal-dialg>
  </div>
</template>

<script>
import Select2 from "v-select2-component";
import LanguagesQuery from "./graphql/LanguagesQuery.gql";
import ListItemWithAction from "./components/list-item-with-action";
import ListItemWithNavigation from "./components/list-item-with-navigation";
import CurrencyQuery from "./graphql/CurrencyQuery.gql";
import {
  isPersisEmployee,
  getUserFullName
} from "@/config/keycloak/keycloakUser.js";
import SetLanguageMutation from "./graphql/SetLanguageMutation.gql";
import SetCurrencyMutation from "./graphql/SetCurrencyMutation.gql";
import { keycloakLogout } from "@/config/keycloak/keycloak";
import Form from "+/components/form/Form";
import ModalDialog from "+/components/dialog/Dialog";

export default {
  components: {
    "a-form": Form,
    "a-modal-dialg": ModalDialog,
    "a-list-item-with-action": ListItemWithAction,
    "a-list-item-with-navigation": ListItemWithNavigation,
    "a-select-2": Select2
  },
  data() {
    return {
      languages: [],
      oldLanguage: "",
      selectedLanguage: "",
      currencies: [],
      selectedCurrency: "",
      passwordCanBeChanged: isPersisEmployee(),
      confirmReload: false
    };
  },
  apollo: {
    $client: "config",
    getLanguages: {
      query: LanguagesQuery,
      manual: true,
      result: function({ data, loading }) {
        if (!loading && data) {
          const activeLanguage = data.getLanguages.find(
            language => language.active
          );
          if (activeLanguage) {
            this.selectedLanguage = activeLanguage.id;
          }

          this.languages = data.getLanguages.map(language => {
            language.disabled = !language.linkedToKeycloak;
            language.selected = language.locale === this.selectedLanguage;
            return language;
          });
        }
      }
    },
    getCurrencies: {
      query: CurrencyQuery,
      manual: true,
      result: function({ data, loading }) {
        if (!loading && data) {
          const activeCurrency = data.getCurrencies.find(
            currency => currency.active
          );
          if (activeCurrency) {
            this.selectedCurrency = activeCurrency.key;
          }
          this.currencies = data.getCurrencies.map(curreny => {
            return {
              id: curreny.key,
              text: curreny.title,
              selected: curreny.active
            };
          });
        }
      }
    }
  },
  watch: {
    selectedLanguage(newValue, oldValue) {
      this.oldLanguage = oldValue;
    }
  },
  methods: {
    renderResult(language) {
      const { title, linkedToKeycloak, element } = language;
      if (element === undefined) {
        return;
      }
      const optionElement = document.createElement("div");
      optionElement.classList.add("d-flex", "justify-content-between");
      const languageElement = document.createElement("span");
      languageElement.innerText = title;
      optionElement.appendChild(languageElement);

      if (!linkedToKeycloak) {
        element.disabled = true;
        const isConfiguredElement = document.createElement("span");
        isConfiguredElement.innerText = this.$t(
          "Config.UserProfile.ProfilePreferences.NoLanguageConfiguration"
        );
        optionElement.appendChild(isConfiguredElement);
      }

      return optionElement;
    },
    setActiveLanguage() {
      this.$apollo
        .mutate({
          $client: "config",
          mutation: SetLanguageMutation,
          variables: {
            id: this.selectedLanguage
          }
        })
        .then(response => {
          if (this.$refs.form.setErrorsIfExsists(response)) {
            this.logout();
          }
        });
    },
    setActiveCurrency() {
      this.$apollo
        .mutate({
          $client: "config",
          mutation: SetCurrencyMutation,
          variables: {
            key: this.selectedCurrency
          }
        })
        .then(response => {
          if (this.$refs.form.setErrorsIfExsists(response)) {
            const successMessage = this.$t(
              "Config.UserProfile.ChangeCurrencySuccessFeedback"
            );
            this.$feedback(successMessage, {
              type: "success"
            });
          } else {
            const errorMessage = this.$t(
              "Config.UserProfile.ChangeCurrencyErrorFeedback"
            );
            this.$feedback(errorMessage, {
              type: "error"
            });
          }
        });
    },
    confirmAbort() {
      this.selectedLanguage = this.oldLanguage;
      this.confirmReload = false;
    },
    logout() {
      const logoutError = () =>
        window.atomlog("Fehler beim Logout aufgetretten", "error");
      keycloakLogout(undefined, logoutError);
    },
    isNotLinkedToKeycloak(language) {
      return !language.linkedToKeycloak;
    },
    getUserFullName() {
      return getUserFullName();
    }
  }
};
</script>
